import { isNotDefined } from '#misc/is-not-defined.guard';

/**
 * Clamps a number to a maximum value.
 *
 * @param value - The number to clamp.
 * @param maximum - The maximum value to clamp to.
 * @returns The clamped value, which will not exceed the maximum.
 */
export function clamp(value: number, maximum: number): number;
/**
 * Clamps a number between a minimum and maximum value.
 *
 * @param value - The number to clamp.
 * @param minimum - The minimum value to clamp to.
 * @param maximum - The maximum value to clamp to.
 * @returns The clamped value, which will not be less than the minimum or greater than the maximum.
 */
export function clamp(value: number, minimum: number, maximum: number): number;
/**
 * Clamps a number to a specified range.
 *
 * - If only `min` is defined, clamps the value to be no less than `min`.
 * - If only `max` is defined, clamps the value to be no greater than `max`.
 * - If both `min` and `max` are defined, clamps the value between `min` and `max`.
 * - If neither is defined, returns the value unchanged.
 *
 * @param value - The number to clamp.
 * @param minimum - The minimum value to clamp to (optional).
 * @param maximum - The maximum value to clamp to (optional).
 * @returns The clamped value.
 */
export function clamp(value: number, minimum: number, maximum?: number) {
  if (isNotDefined(maximum)) {
    return Math.min(value, minimum);
  }

  return Math.min(Math.max(value, minimum), maximum);
}
